import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Tab, TabList, TabPanel, Tabs } from "@admin-ui/components/primitives/application/tabs/tabs";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Head, router, usePage } from "@inertiajs/react";
import { adminListingPath, adminListingsPath, productPath, servicePath } from "@travily/routes";
import type AdminListingsShow from "@travily/types/pages/AdminListingsShow";
import { type ReactNode, useMemo } from "react";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { DetailsTab, ListingMeta, PurchasesTab } from "./show.view";

type AdminListingsShowPageProps = AdminListingsShow;

const VALID_TABS = new Set(["details", "purchases"]);

function resolveTab(url: string): string {
  const raw = new URLSearchParams(url.split("?")[1] ?? "").get("tab") ?? "details";
  return VALID_TABS.has(raw) ? raw : "details";
}

function AdminListingsShowPage({ listing, purchases }: AdminListingsShowPageProps) {
  const account = useAdminLayoutAccount();
  const page = usePage();

  const initialTab = useMemo(() => resolveTab(page.url), [page.url]);

  const publicListingPath =
    listing.published_at == null
      ? null
      : listing.listing_type === "Product"
        ? productPath(listing.sqid)
        : servicePath(listing.sqid);

  return (
    <AdminLayout activeUrl={adminListingsPath()} account={account}>
      <Head title={`${listing.title ?? "Listing"} - Admin`} />

      <AdminPageHeader
        breadcrumbs={[{ label: "Listings", href: adminListingsPath() }, { label: listing.title ?? "Listing" }]}
        title={listing.title ?? "Untitled listing"}
        subtitle={<ListingMeta listing={listing} />}
        actions={
          publicListingPath && (
            <Button size="sm" color="secondary" href={publicListingPath} target="_blank">
              View listing
            </Button>
          )
        }
      />

      <div className="mx-auto w-full max-w-container px-4 lg:px-8">
        <Tabs
          defaultSelectedKey={initialTab}
          onSelectionChange={(key) => {
            router.get(
              adminListingPath(listing.sqid),
              { tab: String(key) },
              { preserveState: true, preserveScroll: true },
            );
          }}
        >
          <TabList orientation="horizontal" type="button-minimal">
            <Tab id="details" label="Details" />
            <Tab id="purchases" label="Purchases" badge={String(purchases.length)} />
          </TabList>

          <TabPanel id="details">
            <DetailsTab listing={listing} />
          </TabPanel>

          <TabPanel id="purchases">
            <PurchasesTab purchases={purchases} />
          </TabPanel>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

AdminListingsShowPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminListingsShowPage;
